import { useCallback, useEffect, useRef, useState, type CSSProperties, type RefObject } from 'react';

export interface UseFixedOnScrollOptions {
    /** Distance from the viewport top at which the element becomes fixed. */
    topOffset?: number;
}

export interface UseFixedOnScrollResult {
    containerRef: RefObject<HTMLDivElement | null>;
    targetRef: RefObject<HTMLDivElement | null>;
    isFixed: boolean;
    className: string;
    style: CSSProperties | undefined;
    placeholderHeight: number;
}

const getRect = (element: HTMLElement | null) => {
    if (!element) {
        return { top: 0, left: 0, width: 0, height: 0 };
    }

    const { top, left, width, height } = element.getBoundingClientRect();
    return { top, left, width, height };
};

export const useFixedOnScroll = (
    fixedClassName: string,
    { topOffset = 0 }: UseFixedOnScrollOptions = {}
): UseFixedOnScrollResult => {
    const containerRef = useRef<HTMLDivElement>(null);
    const targetRef = useRef<HTMLDivElement>(null);
    const [isFixed, setIsFixed] = useState(false);
    const [placeholderHeight, setPlaceholderHeight] = useState(0);
    const [fixedStyle, setFixedStyle] = useState<CSSProperties | undefined>(undefined);

    const updatePosition = useCallback(() => {
        const container = containerRef.current;
        const target = targetRef.current;

        if (!container || !target) {
            return;
        }

        const containerRect = getRect(container);
        const targetRect = getRect(target);
        const shouldFix = containerRect.top <= topOffset;

        setIsFixed((prev) => (prev === shouldFix ? prev : shouldFix));
        setPlaceholderHeight(shouldFix ? targetRect.height : 0);

        if (shouldFix) {
            setFixedStyle({
                '--filter-bar-fixed-top': `${topOffset}px`,
                '--filter-bar-fixed-left': `${containerRect.left}px`,
                '--filter-bar-fixed-width': `${containerRect.width}px`,
            } as CSSProperties);
            return;
        }

        setFixedStyle(undefined);
    }, [topOffset]);

    useEffect(() => {
        const container = containerRef.current;
        const target = targetRef.current;

        if (!container || !target) {
            return;
        }

        let frameId = 0;

        const scheduleUpdate = () => {
            cancelAnimationFrame(frameId);
            frameId = requestAnimationFrame(updatePosition);
        };

        scheduleUpdate();

        document.addEventListener('scroll', scheduleUpdate, { passive: true, capture: true });
        window.addEventListener('resize', scheduleUpdate);

        const resizeObserver = new ResizeObserver(scheduleUpdate);
        resizeObserver.observe(container);
        resizeObserver.observe(target);

        return () => {
            cancelAnimationFrame(frameId);
            document.removeEventListener('scroll', scheduleUpdate, true);
            window.removeEventListener('resize', scheduleUpdate);
            resizeObserver.disconnect();
        };
    }, [updatePosition]);

    return {
        containerRef,
        targetRef,
        isFixed,
        className: isFixed ? fixedClassName : '',
        style: fixedStyle,
        placeholderHeight,
    };
};
